/**
 * ClinicalDesk view (PROD-579 + PROD-569 + PROD-570 + PROD-588 + PROD-590).
 *
 * Phase 1 + Phase 2 entry: mounts the PatientPicker on the left rail,
 * renders the canonical chart-header `<SyntheticBadge variant="header">`
 * (mitigation 1), surfaces the active patient's name through
 * `<PatientNameTag>` (mitigation 2), renders the
 * `<IllustrativeProjectionBanner>` Layer 1 safety surface above the chart
 * panel (PROD-570), and (PROD-588 entry) renders the active patient's NBA
 * candidates from `useNbaStore` as `<NbaCard>` primitives.
 *
 * The NBA section is `<section aria-labelledby="nba-section-heading">`
 * carrying the active patient's NBA candidate list. Each card opens the
 * `<OverrideModal>` on click of its Override button; submit chains
 * through `useNbaStore.applyOverride` which delegates to the learning-loop
 * service (PROD-572). The candidate visually shifts to `state: 'reduced'`
 * (dimmed pill + REDUCED status badge) WITHOUT being removed from the
 * list (HC8 no-over-suppression).
 *
 * PROD-590 RAG citations: every NBA candidate runs through the citation
 * resolver (`resolveCitationsForNba`) so each card carries a `CitationSet`
 * surfaced as the Sources strip. Resolution is mode-aware: Live Mode hits
 * the corpus retriever (or surfaces the empty-state when retrieval fails);
 * Demo Mode short-circuits to the candidate fixture citations. Results
 * cache by `(candidateId, mode)` to avoid redundant retrievals on every
 * render. HC3 invariant: an NBA card never renders without a citation
 * surface (live, demo, or empty-state).
 *
 * Phase 2 will replace the placeholder body summary with the full SOAP
 * shell, vitals, labs, and ICD-10 rows. The NBA section itself ships now.
 *
 * Gotcha 1 + 2 (setInterval ghost-write + uncancelled typewriter chain) are
 * fixed via useEffect cleanup + AbortController per COMPONENT-CONTRACTS
 * §Gotchas.
 */
import { useEffect, useState } from 'react';
import { IllustrativeProjectionBanner } from '../components/IllustrativeProjectionBanner';
import { NbaCard, type NbaCardCandidate } from '../components/NbaCard';
import { PatientPicker } from '../components/PatientPicker';
import { PatientNameTag } from '../components/PatientNameTag';
import { SyntheticBadge } from '../components/SyntheticBadge';
import { PATIENT_REGISTRY } from '../data/synthetic-patients';
import { usePatientStore } from '../state/patient.store';
import { useNbaStore } from '../state/nba.store';
import { useTenantStore } from '../state/tenant.store';
import { useModeStore } from '../state/mode.store';
import {
  resolveCitationsForNba,
  type CitationSet,
} from '../services/citation-resolver';
import type { OverrideEvent } from '../types/override';
import type { Mode } from '../state/types';

const NBA_SECTION_HEADING = 'AI suggestions for this patient';
const NBA_EMPTY_STATE_MESSAGE =
  'No AI suggestions for this patient yet. The pathway engine will surface candidates as the encounter advances.';

// Stable empty array reference so the Zustand selector below does not
// produce a fresh value on every render when the active patient has no
// candidates. Without this guard React 18's `useSyncExternalStore`
// detects a changed snapshot and triggers an infinite re-render loop.
const EMPTY_CANDIDATES: ReadonlyArray<NbaCardCandidate> = Object.freeze([]);

/**
 * Cache key for the resolved citation set per candidate per mode. Lifted
 * to a stable function so memoization stays correct across re-renders.
 */
function citationCacheKey(candidateId: string, mode: Mode): string {
  return `${mode}:${candidateId}`;
}

export function ClinicalDesk() {
  const activePatient = usePatientStore((s) => s.getActivePatient());
  const activePatientId = usePatientStore((s) => s.activePatientId);
  const setActivePatient = usePatientStore((s) => s.setActivePatient);
  const tenantId = useTenantStore((s) => s.tenantId);
  const mode = useModeStore((s) => s.mode);
  const candidatesByPatient = useNbaStore((s) => s.candidatesByPatient);
  const candidates = activePatient
    ? candidatesByPatient[activePatient.id] ?? EMPTY_CANDIDATES
    : EMPTY_CANDIDATES;
  const applyOverride = useNbaStore((s) => s.applyOverride);

  // Resolved citation cache keyed by `<mode>:<candidateId>`. Resolution
  // runs once per (mode, candidateId) pair; cached results survive
  // override-driven candidate replacement (the candidate id is stable
  // across reductions) so the strip + drawer do not re-fetch.
  const [citationCache, setCitationCache] = useState<Record<string, CitationSet>>(
    {},
  );

  useEffect(() => {
    if (!activePatient) return;
    let cancelled = false;
    for (const candidate of candidates) {
      const key = citationCacheKey(candidate.id, mode);
      if (citationCache[key]) continue;
      // Live retrieval deps are deferred until the INFRA GATE adds the
      // pg + openai npm deps and provisions the corpus table. Until then
      // the resolver receives `deps: null` for Live Mode, which surfaces
      // the credentials empty-state via HC3 rather than crashing.
      void resolveCitationsForNba(candidate, activePatient.id, mode, {
        deps: null,
      }).then((set) => {
        if (cancelled) return;
        setCitationCache((prev) => {
          const k = citationCacheKey(candidate.id, mode);
          if (prev[k]) return prev;
          return { ...prev, [k]: set };
        });
      });
    }
    return () => {
      cancelled = true;
    };
  }, [candidates, activePatient, mode, citationCache]);

  function handleOverride(event: OverrideEvent, candidate: NbaCardCandidate) {
    if (!activePatient) return;
    applyOverride(activePatient.id, candidate.id, {
      pathway_id: event.pathway_id,
      step_id: event.step_id,
      nba_id: event.nba_id,
      tenant_id: tenantId,
      reason_category: event.reason_category,
      correlation_id: event.correlation_id,
      ts: event.ts,
    });
  }

  return (
    <section
      aria-labelledby="view-title-emr"
      className="flex min-h-[calc(100vh-7rem)]"
    >
      <PatientPicker
        patients={PATIENT_REGISTRY}
        activePatientId={activePatientId}
        onSelect={setActivePatient}
      />
      <div className="flex-1 px-gutter-lg py-gutter-xl">
        <div className="flex items-center justify-between gap-gutter-md">
          <h2
            id="view-title-emr"
            className="text-signal-2xl font-semibold text-text-primary"
          >
            ClinicalDesk
          </h2>
          <SyntheticBadge variant="header" />
        </div>
        <IllustrativeProjectionBanner
          variant="block-prefix"
          className="mt-gutter-md max-w-2xl"
        />
        {activePatient ? (
          <div className="mt-gutter-lg">
            <p
              data-active-patient-id={activePatient.id}
              data-active-patient-pathway={activePatient.pathway}
              className="text-signal-lg"
            >
              <PatientNameTag name={activePatient.name} variant="clinician" />
            </p>
            <p className="mt-gutter-sm text-signal-base text-text-secondary">
              {activePatient.summary}
            </p>
            <NbaSection
              candidates={candidates}
              patientId={activePatient.id}
              mode={mode}
              citationCache={citationCache}
              onOverride={handleOverride}
            />
          </div>
        ) : (
          <p className="mt-gutter-md max-w-2xl text-signal-base text-text-secondary">
            No active patient. Select one from the picker on the left.
          </p>
        )}
      </div>
    </section>
  );
}

interface NbaSectionProps {
  candidates: ReadonlyArray<NbaCardCandidate>;
  patientId: string;
  mode: Mode;
  citationCache: Record<string, CitationSet>;
  onOverride: (event: OverrideEvent, candidate: NbaCardCandidate) => void;
}

function NbaSection({
  candidates,
  patientId,
  mode,
  citationCache,
  onOverride,
}: NbaSectionProps) {
  return (
    <section
      aria-labelledby="nba-section-heading"
      data-testid="nba-section"
      data-nba-candidate-count={candidates.length}
      className="mt-gutter-lg flex flex-col gap-gutter-md"
    >
      <h3
        id="nba-section-heading"
        className="text-signal-lg font-semibold text-text-primary"
      >
        {NBA_SECTION_HEADING}
      </h3>
      {candidates.length === 0 ? (
        <p
          data-testid="nba-section-empty-state"
          className="max-w-2xl text-signal-base text-text-secondary"
        >
          {NBA_EMPTY_STATE_MESSAGE}
        </p>
      ) : (
        <ul
          data-testid="nba-section-list"
          className="flex flex-col gap-gutter-sm"
        >
          {candidates.map((c, idx) => (
            <li key={c.id}>
              <NbaCard
                candidate={c}
                patientId={patientId}
                onOverride={onOverride}
                citations={citationCache[citationCacheKey(c.id, mode)]}
                isWalkthroughAnchor={idx === 0}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export { NBA_SECTION_HEADING, NBA_EMPTY_STATE_MESSAGE };
